import React from 'react';
import {
  Snackbar,
  Alert,
  AlertTitle,
  Slide,
  Grow,
  Fade,
  Box
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';

// Transition components
const SlideTransition = (props) => {
  return <Slide {...props} direction="down" />;
};

const GrowTransition = (props) => {
  return <Grow {...props} />;
};

const FadeTransition = (props) => {
  return <Fade {...props} />;
};

const CustomAlert = ({
  open,
  onClose,
  severity = 'info', // 'success', 'error', 'warning', 'info'
  message,
  title,
  autoHideDuration = 4000,
  position = 'top-right', // 'top-right', 'top-center', 'top-left', 'bottom-right', 'bottom-center', 'bottom-left'
  transition = 'slide', // 'slide', 'grow', 'fade'
  variant = 'filled', // 'filled', 'outlined', 'standard'
  showIcon = true,
  closable = true,
  width = 'auto', // 'auto', 'sm', 'md', 'lg', 'full'
}) => {
  // Get position props for Snackbar
  const getAnchorOrigin = () => {
    const positions = {
      'top-right': { vertical: 'top', horizontal: 'right' },
      'top-center': { vertical: 'top', horizontal: 'center' },
      'top-left': { vertical: 'top', horizontal: 'left' },
      'bottom-right': { vertical: 'bottom', horizontal: 'right' },
      'bottom-center': { vertical: 'bottom', horizontal: 'center' },
      'bottom-left': { vertical: 'bottom', horizontal: 'left' },
    };
    return positions[position] || positions['top-right'];
  };

  // Get transition component
  const getTransitionComponent = () => {
    const transitions = {
      slide: SlideTransition,
      grow: GrowTransition,
      fade: FadeTransition,
    };
    return transitions[transition] || SlideTransition;
  };

  // Get custom icons
  const getIcon = () => {
    const icons = {
      success: <CheckCircleIcon fontSize="inherit" />,
      error: <ErrorIcon fontSize="inherit" />,
      warning: <WarningIcon fontSize="inherit" />,
      info: <InfoIcon fontSize="inherit" />,
    };
    return icons[severity];
  };

  // Get width style
  const getWidth = () => {
    const widths = {
      auto: 'auto',
      sm: '300px',
      md: '400px',
      lg: '500px',
      full: '90vw',
    };
    return widths[width] || 'auto';
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={autoHideDuration}
      onClose={onClose}
      anchorOrigin={getAnchorOrigin()}
      TransitionComponent={getTransitionComponent()}
      sx={{
        '& .MuiSnackbar-root': {
          top: position.includes('top') ? '24px' : 'auto',
          bottom: position.includes('bottom') ? '24px' : 'auto',
        }
      }}
    >
      <Alert
        severity={severity}
        variant={variant}
        icon={showIcon ? getIcon() : false}
        onClose={closable ? onClose : undefined}
        sx={{
          width: getWidth(),
          minWidth: '280px',
          maxWidth: '600px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
          borderRadius: '8px',
          '& .MuiAlert-message': {
            width: '100%',
            padding: '8px 0',
          },
          '& .MuiAlert-icon': {
            fontSize: '24px',
            marginRight: '12px',
          },
          // Custom styles for different variants
          ...(variant === 'filled' && {
            '&.MuiAlert-filledSuccess': {
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            },
            '&.MuiAlert-filledError': {
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
            },
            '&.MuiAlert-filledWarning': {
              background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
            },
            '&.MuiAlert-filledInfo': {
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            },
          }),
          ...(variant === 'outlined' && {
            borderWidth: '2px',
          }),
        }}
      >
        {title && (
          <AlertTitle sx={{ fontWeight: 600, marginBottom: '4px' }}>
            {title}
          </AlertTitle>
        )}
        <Box sx={{ fontSize: '14px', lineHeight: 1.5 }}>
          {message}
        </Box>
      </Alert>
    </Snackbar>
  );
};

export default CustomAlert;